import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingCatalogPanel.scss';

import { updateBrandOnboardingObject } from '../../../Actions/OnboardingActions';
import { addCatalogSyncTask, fetchCatalogSyncTasksStatus } from '../../../APIClient/catalog_tasks';
import { fetchCatalogVariants } from '../../../APIClient/variants';
import { getBrandId } from '../../../Helpers/user_helpers';

import { CATALOG_VARIANTS_PAGE_SIZE } from '../../Catalog/Constants/catalog';
import ChatConnect from '../../Chat/ChatConnect';

import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';

const BrandOnboardingCatalogPanel = props => {
  const { user, onboarding } = props;
  const CATALOG_SYNC_TASK = 'sync_catalog_products';

  const [isSyncingCatalogProducts, setIsSyncingCatalogProducts] = useState(false);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [brandVariants, setBrandVariants] = useState([]);
  const catalogSyncTasksWS = useRef(null);

  const startCatalogSyncTask = async () => {
    setIsSyncingCatalogProducts(true);

    try {
      await addCatalogSyncTask({ Brand_id: getBrandId(user), catalogTaskType: CATALOG_SYNC_TASK });
    } catch (err) {
      if (err?.toLowerCase().includes('catalog task for this brand is already in progress.')) {
        console.info('Registered catalog sync task already in progress');
      } else {
        console.error('Error starting catalog sync task: ', err);
        window.ALERT.error(
          'There was an issue beginning your Catalog sync. If this issue persists, please reach out to your Account Manager for a resolution.'
        );
        setIsSyncingCatalogProducts(false);
      }
    }
  };

  const initCatalogSyncTasksData = async () => {
    try {
      const catalogSyncTasksResp = await fetchCatalogSyncTasksStatus(getBrandId(user));
      const activeCatalogSyncTasks = catalogSyncTasksResp.catalogTasks;
      const isAlreadySyncingBrandCatalog = activeCatalogSyncTasks.find(task => task.type === 'sync_catalog_products');
      if (isAlreadySyncingBrandCatalog) setIsSyncingCatalogProducts(true);
      else startCatalogSyncTask({ Brand_id: getBrandId(user) });
    } catch (err) {
      console.error('Error polling catalog sync tasks: ', err);
      window.ALERT.error('There was an issue checking the synced status of your Catalog. Syncing is currently unavailable.', {
        hideAfter: 10
      });
    }
  };

  const fetchBrandVariants = async () => {
    setIsLoadingVariants(true);

    try {
      const Brand_id = getBrandId(user);
      const fetchParams = { limit: CATALOG_VARIANTS_PAGE_SIZE, offset: 0 };
      const { variants } = await fetchCatalogVariants(Brand_id, fetchParams);

      if (!variants?.length) startCatalogSyncTask();
      else setBrandVariants(variants);
    } catch (error) {
      console.error('Error fetching variants:', error);
      window.ALERT.error('There was an issue fetching your product catalog. Please try again.');
    } finally {
      setIsLoadingVariants(false);
    }
  };

  useEffect(() => {
    if (!getBrandId(user)) return window.ALERT.error('Please login to your account to set up your catalog.');

    // fetchBrandVariants();
  }, []);

  const onWebsocketConnect = () => {
    if (false) {
      initCatalogSyncTasksData();
    }
  };

  const onWebsocketStatusUpdate = async data => {
    if (data.Brand_id !== getBrandId(user)) return;

    if (data.status_enum === 'COMPLETE') {
      window.ALERT.success(data.status_display, { hideAfter: 10 });
      setIsSyncingCatalogProducts(false);

      fetchBrandVariants();
    } else if (data.status_enum === 'FAILURE') {
      window.ALERT.error(data.status_display, { hideAfter: 20 });
      setIsSyncingCatalogProducts(false);
    }
  };

  const onWebsocketDisconnect = () => {
    // setIsCatalogSyncStatusInitialized(false);
  };

  return (
    <div className='brand-onboarding-catalog-panel-outer'>
      <div className='brand-onboarding-catalog-panel-inner'>
        <ChatConnect
          user={user}
          setWebsocket={initializedWS => (catalogSyncTasksWS.current = initializedWS)}
          onConnect={onWebsocketConnect}
          onFailedAttempt={onWebsocketDisconnect}
          onFailMessage={'There was an issue checking the synced status of your Catalog. Syncing is currently unavailable.'}
          onStatusUpdate={onWebsocketStatusUpdate}
          receivedMessage={() => {}}
        />

        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-container-inner'>
                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>

              <div className='brand-onboarding-graphic-background'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay'></div>
              </div>
            </div>

            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Looks like we can’t set up your catalog yet...</div>
                <div className='brand-onboarding-subtitle-text'>
                  Our team will reach out within the next few business days asking for a complete list of products you provide in your catalog. The
                  most common ways we host catalogs are through FTP, SFTP, or a Google Sheet.
                </div>

                <div className='brand-onboarding-pagination-buttons'>
                  <button className='brand-onboarding-button stretch' onClick={() => props.goToPreviousPanel()}>
                    Back
                  </button>
                  <button className='brand-onboarding-button stretch primary' onClick={() => props.goToNextPanel()}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingCatalogPanel.propTypes = {
  user: PropTypes.object.isRequired,
  onboarding: PropTypes.object.isRequired,
  updateBrandOnboardingObject: PropTypes.func.isRequired,

  goToPreviousPanel: PropTypes.func.isRequired,
  goToNextPanel: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  user: state.user,
  onboarding: state.onboarding
});

export default connect(mapStateToProps, {
  updateBrandOnboardingObject
})(withRouter(BrandOnboardingCatalogPanel));
